/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { v4 as uuid } from 'uuid';

import { EmitterMixin } from 'ckeditor5/src/utils.js';

import MessagesCompressor from './../messagescompressor';

import Sessions from './../sessions/sessions';

import CollaborativeEditingConnectMessage from './messages/collaborativeeditingconnectmessage';
import CollaborativeEditingUpdateMessage from './messages/collaborativeeditingupdatemessage';
import CollaborativeEditingReconnectMessage from './messages/collaborativeeditingreconnectmessage';
import CollaborativeEditingResponse from './responses/collaborativeeditingresponse';
import CollaborativeEditingConnectResponse from './responses/collaborativeeditingconnectresponse';

import WebSocketGateway, { IWebSocketGateway } from './../websocketgateway/websocketgateway';

import CKEditorCloudServicesError from '../ckeditorcloudserviceserror';
import Channel from '../websocketgateway/channel';
import SessionCollection from '../sessions/sessioncollection';
import ServiceNotConnectedError from '../errors/ServiceNotConnectedError';
import GetDocumentDetailsResponse from './responses/getdocumentdetailsresponse';
import GetDocumentDetailsMessage from './messages/getdocumentdetailsmessage';

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _SERVICE: number = 1;

export interface IOperationsData {
	buffers: ( Buffer | Uint8Array )[];
	types: number[];
}

/**
 * General purpose CollaborativeEditingService used to synchronize data store values with other collaborative nodes.
 */
// eslint-disable-next-line new-cap
class CollaborativeEditingService extends /* #__PURE__ -- @preserve */ EmitterMixin() {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public static _SERVICE: number = _SERVICE;

	private readonly _bundleVersion: string;

	private readonly _id: string;

	private _isConnected: boolean;

	private _wsGateway?: IWebSocketGateway;

	private _channel?: Channel;

	private _connectedSessions?: SessionCollection;

	/**
	 * Create CollaborativeEditingService instance.
	 *
	 *     const collabWritingService = new CollaborativeEditingService( '0.9.0', 'post-456-text' );
	 *
	 * @throws {TypeError}
	 * Throws when bundleVersion param is not provided.
	 */
	public constructor( bundleVersion: string, serviceId?: string ) {
		super();

		if ( !bundleVersion ) {
			throw new TypeError( 'Param "bundleVersion" must be provided.' );
		}

		this._id = serviceId ?? uuid();

		this._isConnected = false;

		this._bundleVersion = bundleVersion;
	}

	public getId(): string {
		return this._id;
	}

	/**
	 * Indicates that CollaborativeEditingService is connected to CKEditor Cloud Services.
	 */
	public isConnected(): boolean {
		return this._isConnected;
	}

	/**
	 * Connects CollaborativeEditingService to CKEditor Cloud Services.
	 *
	 *     const myDocument = new CollaborativeEditingService( '0.9.0', 'article-1234' );
	 *
	 *     await myDocument.connect( wsGateway );
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public connect(
		wsGateway: IWebSocketGateway,
		data: { buffers: Buffer[]; types: number[]; } = { buffers: [], types: [] },
		lastOperationId?: string
	): Promise<CollaborativeEditingConnectResponse | void> {
		const message: CollaborativeEditingConnectMessage = new CollaborativeEditingConnectMessage(
			this.getId(),
			data.buffers,
			data.types,
			this._bundleVersion,
			lastOperationId
		);

		return this._connect( wsGateway, message );
	}

	/**
	 * Reconnects CollaborativeEditingService to CKEditor Cloud Services.
	 *
	 *     const myDocument = new CollaborativeEditingService( '0.9.0', 'article-1234' );
	 *
	 *     await myDocument.connect( wsGateway );
	 *
	 *     wsGateway.on( 'change:state', ( event, property, value ) => {
	 *     		 if( value === 'disconnected' ) {
	 *     		 	await myDocument.reconnect( wsGateway, 123 );
	 *     		 }
	 *     } );
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Thrown when connection already exists or in the case of errors on the backend side.
	 */
	public reconnect( wsGateway: IWebSocketGateway, lastKnowVersion: number ): Promise<CollaborativeEditingConnectResponse> {
		if ( this.isConnected() ) {
			throw new CKEditorCloudServicesError( 'Cannot reconnect to already connected service.', wsGateway );
		}

		return this._connect(
			wsGateway,
			new CollaborativeEditingReconnectMessage( this.getId(), lastKnowVersion, this._bundleVersion )
		) as Promise<CollaborativeEditingConnectResponse>;
	}

	/**
	 * Disconnects CollaborativeEditingService from CKEditor Cloud Services.
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public disconnect(): void {
		if ( !this._isConnected ) {
			return;
		}

		this._isConnected = false;

		if ( this._wsGateway ) {
			this.stopListening( this._wsGateway );

			this._wsGateway = undefined;
		}

		if ( this._channel ) {
			this.stopListening( this._channel );

			this._channel = undefined;
		}

		if ( this._connectedSessions ) {
			this._connectedSessions.disconnect();

			this._connectedSessions = undefined;
		}

		this.fire( 'disconnected' );

		this.stopListening();
	}

	/**
	 * Gets document details from CKEditor Cloud Services.
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public async getDocumentDetails(): Promise<GetDocumentDetailsResponse> {
		const message: GetDocumentDetailsMessage = new GetDocumentDetailsMessage(
			this.getId()
		);

		if ( !this._wsGateway ) {
			throw new ServiceNotConnectedError( 'Collaborative Editing', this );
		}

		const response: Buffer = await this._wsGateway._sendRequest(
			_SERVICE,
			GetDocumentDetailsMessage.TYPE,
			MessagesCompressor.encode( message )
		);

		return MessagesCompressor.decode( response, GetDocumentDetailsResponse ) as GetDocumentDetailsResponse;
	}

	/**
	 * Sends update to connected service. This will propagate update to other connected CollaborativeEditingService.
	 * Data update will not be stored on CollaborativeEditingService server instance.
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Throws when baseVersion or data is not provided, data is empty, or in the case of errors on the backend side.
	 *
	 * @throws {ServiceNotConnectedError}
	 * Throws when collaborative editing service is not connected.
	 */
	public async sendOperations(
		data: IOperationsData,
		baseVersion: number | string,
		lastOperationId: string
	): Promise<CollaborativeEditingResponse> {
		if ( !data?.types?.length ) {
			throw new CKEditorCloudServicesError( 'Cannot send empty update.', this._wsGateway );
		}

		const parsedBaseVersion: number = typeof baseVersion === 'number' ? baseVersion : parseInt( baseVersion );

		if ( !Number.isInteger( parsedBaseVersion ) || parsedBaseVersion < 0 ) {
			throw new CKEditorCloudServicesError( 'Base version not provided.', this._wsGateway );
		}

		const message: CollaborativeEditingUpdateMessage = new CollaborativeEditingUpdateMessage(
			this.getId(),
			data.buffers,
			data.types,
			parsedBaseVersion,
			[],
			lastOperationId
		);

		if ( !this._wsGateway || !this._isConnected ) {
			throw new ServiceNotConnectedError( 'Collaborative Editing', this );
		}

		const response: Buffer = await this._wsGateway._sendRequest(
			_SERVICE,
			CollaborativeEditingUpdateMessage.TYPE,
			MessagesCompressor.encode( message )
		);

		return MessagesCompressor.decode( response, CollaborativeEditingResponse ) as CollaborativeEditingResponse;
	}

	/**
	 * Returns read-only observable collection with EndUsers Sessions for collaborative editing service.
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Thrown in the case of errors on the backend side.
	 */
	public async getConnectedSessions(): Promise<SessionCollection> {
		if ( !this._isConnected ) {
			throw new ServiceNotConnectedError( 'Collaborative Editing', this );
		}

		if ( !this._connectedSessions ) {
			this._connectedSessions = await Sessions.getConnectedSessions(
				this._wsGateway!,
				this._id,
				_SERVICE
			) as SessionCollection;
		}

		return this._connectedSessions;
	}

	/**
	 * Returns a Promise that resolves with read-only observable collection with EndUsers Sessions for given id.
	 *
	 *    const sessions = await CollaborativeEditingService.getConnectedSessions( 'my-document-id' );
	 *
	 * @deprecated Use not static version of getConnectedSessions method.
	 *
	 * @throws {CKEditorCloudServicesError}
	 * Throws in the case of errors on the backend side.
	 */
	public static getConnectedSessions( wsGateway: IWebSocketGateway, id: string ): Promise<SessionCollection> {
		return Sessions.getConnectedSessions( wsGateway, id, _SERVICE );
	}

	// eslint-disable-next-line @typescript-eslint/naming-convention
	public async _connect(
		wsGateway: IWebSocketGateway,
		message: CollaborativeEditingConnectMessage | CollaborativeEditingReconnectMessage
	): Promise<CollaborativeEditingConnectResponse | void> {
		if ( this.isConnected() ) {
			return;
		}

		if ( wsGateway.state !== WebSocketGateway.STATE_CONNECTED ) {
			throw new CKEditorCloudServicesError( 'WebSocket Gateway is not connected.', wsGateway );
		}

		this._wsGateway = wsGateway;
		this.stopListening( wsGateway, 'change:state' );

		const serviceResponse: Buffer = await wsGateway._sendRequest(
			_SERVICE,
			message.constructor.TYPE as string,
			MessagesCompressor.encode( message )
		);

		const collaborativeEditingConnectResponse: CollaborativeEditingConnectResponse = MessagesCompressor.decode(
			serviceResponse,
			CollaborativeEditingConnectResponse
		) as CollaborativeEditingConnectResponse;

		this.listenTo(
			wsGateway,
			'change:state',
			// eslint-disable-next-line @typescript-eslint/naming-convention
			( _1, _2, value ) => this._onWsGatewayStateChange( value ),
			{ priority: WebSocketGateway._CHANGE_STATE_EVENT_PRIORITY }
		);

		this._connectToChannel( wsGateway, collaborativeEditingConnectResponse.channel );

		this._isConnected = true;
		this.fire( 'connected' );

		return collaborativeEditingConnectResponse;
	}

	/**
	 * Connects given CollaborativeEditingService to channel.
	 */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	protected _connectToChannel( wsGateway: IWebSocketGateway, channelName: string ): void {
		this._channel = wsGateway._getChannel( _SERVICE, channelName );

		this.listenTo(
			this._channel,
			this._channel.getEventName( CollaborativeEditingUpdateMessage.TYPE ),
			( event, data ) => {
				const updateMessage: CollaborativeEditingUpdateMessage = MessagesCompressor.decode(
					data,
					CollaborativeEditingUpdateMessage
				) as CollaborativeEditingUpdateMessage;

				this.fire(
					'operationsReceived',
					updateMessage.baseVersion,
					updateMessage.data,
					updateMessage.metadata
				);
			}
		);
	}

	/**
	 * WebSocketGateway "change:state" events handler.
	 */
	private _onWsGatewayStateChange( value: unknown ): void {
		if ( value === WebSocketGateway.STATE_DISCONNECTED ) {
			this.disconnect();
		}
	}
}

export default CollaborativeEditingService;
